import { useMemo } from 'react'
import { BanIcon, CheckIcon } from 'lucide-react'

import { displayProgressStepLabel } from '@/utilities/billTrack50/getBillProgressTimeline'
import { dateToLocaleString } from '@/utilities/dateToLocaleString'
import { cn } from '@/utilities/ui'

import type { BillProgressTimelineEntry } from '@/utilities/billTrack50/getBillProgressTimeline'

/**
 * Bill progress timeline.
 * Steps stack until the component is at least 37.5rem wide (five steps at 7.5rem / 120px),
 * then share a row. That follows the bill column, which narrows beside the sidebar and
 * widens again when the sidebar drops below the content. Connectors run to the circle
 * centers, behind the bubbles, so the line meets the bubble edge.
 */
export const BillStatus = ({ billStatus }: { billStatus: BillProgressTimelineEntry[] }) => {
  const firstActiveIndex = useMemo(() => {
    return billStatus.reduce<number | null>(
      (acc, status, index) => (status.completed === false && acc === null ? index : acc),
      null,
    )
  }, [billStatus])

  return (
    <div className="@container/bill-status my-6 w-full">
      <div className="flex w-full flex-col @bill-row/bill-status:flex-row @bill-row/bill-status:items-start">
        {billStatus.map((status, index) => (
          <div
            key={`${status.label}-${index}`}
            className="relative flex items-start gap-4 font-body @bill-row/bill-status:min-w-30 @bill-row/bill-status:flex-1 @bill-row/bill-status:flex-col @bill-row/bill-status:items-center @bill-row/bill-status:gap-0 @bill-row/bill-status:text-center"
          >
            {index < billStatus.length - 1 && (
              <div
                aria-hidden
                className="absolute top-12 bottom-0 left-6 w-px -translate-x-1/2 bg-accent-foreground @bill-row/bill-status:hidden"
              />
            )}
            {index > 0 && (
              <div
                aria-hidden
                className="absolute top-9 right-1/2 z-0 hidden h-px w-full bg-accent-foreground @bill-row/bill-status:block"
              />
            )}
            <span
              className={cn(
                'relative z-10 flex size-12 shrink-0 items-center justify-center rounded-full border border-evergreen-300 bg-white @bill-row/bill-status:mb-2.5 @bill-row/bill-status:size-18',
                status.status === 'failed' && 'border-tuscany-300',
              )}
            >
              <span
                className={cn(
                  'absolute inset-4 top-1/2 left-1/2 z-1 block size-[calc(100%-8px)] -translate-1/2 rounded-full',
                  status.completed
                    ? status.status === 'failed'
                      ? 'bg-tuscany-200'
                      : 'bg-evergreen-100'
                    : 'bg-evergreen-300',
                )}
              />

              {index === firstActiveIndex && (
                <span className="absolute top-1/2 left-1/2 z-1 block size-3 -translate-1/2 animate-[ping-large_3s_cubic-bezier(0,0,0.2,1)_infinite] rounded-full bg-evergreen-400 opacity-90 motion-reduce:hidden @bill-row/bill-status:size-4" />
              )}

              {status.completed ? (
                status.status === 'failed' ? (
                  <BanIcon className="relative z-2 size-6 text-tuscany-600 @bill-row/bill-status:size-8" />
                ) : (
                  <CheckIcon className="relative z-2 size-6 text-accent @bill-row/bill-status:size-8" />
                )
              ) : (
                <span className="relative z-2 block size-3 rounded-full bg-accent @bill-row/bill-status:size-4" />
              )}
            </span>
            <div
              className={cn(
                'min-w-0 pt-3 @bill-row/bill-status:w-full @bill-row/bill-status:pt-0',
                index < billStatus.length - 1 && 'pb-6 @bill-row/bill-status:pb-0',
              )}
            >
              <p className="m-0 p-0 text-sm font-semibold wrap-break-word text-primary @bill-row/bill-status:text-base">
                {displayProgressStepLabel(status.label)}
              </p>
              <p className="m-0 p-0 text-sm font-medium wrap-break-word text-muted-foreground">
                {status.completed
                  ? status.date
                    ? dateToLocaleString(status.date)
                    : ''
                  : 'Pending'}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
